/**
 * FASE E.2 Turno 3 — Admin Case Editor.
 *
 * Loads the current active revision snapshot, lets an admin correct INPUT
 * DATA ONLY across the 8 sections, previews a human-readable diff, runs the
 * consistency pre-check server-side (via the revision endpoint) and creates
 * a new immutable revision. Optimistic locking handled via `base_revision_number`.
 *
 * NO methodology / scoring / consistency logic here — the editor is a thin
 * data-entry layer over the same enums and structural flags the Wizard uses.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronRight, Save, Trash2, Plus, Loader2, ShieldCheck,
  RefreshCw, AlertTriangle, CheckCircle2, MessageSquare, Lock, Info,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import {
  adminGetRevision, adminListRevisions, adminCreateRevision,
  adminListNotes, adminCreateNote, adminUpdateNote, api,
} from "@/lib/api";
import {
  DOC_TYPES, QDOC_ROLES, QDOC_ORIGINAL_STATUS, QDOC_MATERIAL, SOURCE_MATERIALS,
  DATE_PRECISION_OPTIONS, ASSISTED_PARTY_ROLES, PRIVATE_CASE_ROLES, LEGAL_TITLES,
  PHASE_OPTIONS, SIGNATURE_STATUS_OPTIONS, TIMELINE_MACROS, CLINICAL_DOC_TYPES,
  CLINICAL_CONDITIONS, mkId, humanizeFieldPath, humanizeValue,
} from "@/lib/adminEditorConstants";

const SECTIONS = [
  { id: "requester", label: "Richiedente" },
  { id: "assisted", label: "Assistito & Scrivente" },
  { id: "qdocs", label: "Documenti in verifica" },
  { id: "comparatives", label: "Comparative" },
  { id: "timeline", label: "Cronologia" },
  { id: "clinical", label: "Clinico" },
  { id: "strategy", label: "Strategia" },
  { id: "notes", label: "Note consulente" },
];

// FASE E.2 Turno 3 — Ensure list items have stable ids. Legacy items saved
// before D.4 might lack `.id`, which would make the diff unstable.
function ensureIds(items, prefix) {
  if (!Array.isArray(items)) return items;
  return items.map((x) =>
    x && typeof x === "object" && !x.id ? { ...x, id: mkId(prefix) } : x
  );
}

function normalizeCase(c) {
  const out = { ...c };
  const comp = { ...(out.comparatives || {}) };
  comp.comparatives = ensureIds(comp.comparatives || [], "cmp");
  out.comparatives = comp;
  out.timeline = ensureIds(out.timeline || [], "ev");
  out.questioned_documents = ensureIds(out.questioned_documents || [], "qd");
  const cl = { ...(out.clinical || {}) };
  cl.documents = ensureIds(cl.documents || [], "clindoc");
  cl.ricoveri = ensureIds(cl.ricoveri || [], "ricov");
  cl.conditions = cl.conditions || [];
  out.clinical = cl;
  out.requester = { ...(out.requester || {}) };
  out.assisted_party = { ...(out.assisted_party || {}) };
  out.identification = { ...(out.identification || {}) };
  out.identification.subject = { ...(out.identification.subject || {}) };
  // FASE E.2 Turno 3.5 HOTFIX — sanitise legacy ISO timestamps on the
  // primary DOC-date so `<input type="date">` (which requires strict
  // `YYYY-MM-DD`) shows the value and accepts edits. Without this the
  // field appeared empty live and the admin could not type into it.
  const _isoDate = (v) => {
    if (!v || typeof v !== "string") return v || "";
    const m = v.match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : "";
  };
  if (out.identification.contested_document_date) {
    out.identification.contested_document_date = _isoDate(out.identification.contested_document_date);
  }
  out.legal_strategy = { ...(out.legal_strategy || {}) };
  return out;
}

// Flat diff (mirrors backend `_diff_paths`) — used by the diff modal.
function diffPaths(before, after, path = "") {
  const out = [];
  const isDict = (x) => x && typeof x === "object" && !Array.isArray(x);
  if (isDict(before) || isDict(after)) {
    const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
    for (const k of [...keys].sort()) {
      const sub = path ? `${path}.${k}` : k;
      out.push(...diffPaths((before || {})[k], (after || {})[k], sub));
    }
    return out;
  }
  if (Array.isArray(before) || Array.isArray(after)) {
    const bl = (before || []).length;
    const al = (after || []).length;
    for (let i = 0; i < Math.max(bl, al); i++) {
      const b = i < bl ? before[i] : undefined;
      const a = i < al ? after[i] : undefined;
      out.push(...diffPaths(b, a, `${path}[${i}]`));
    }
    return out;
  }
  const norm = (v) => (v === undefined ? null : v);
  if (JSON.stringify(norm(before)) !== JSON.stringify(norm(after))) {
    out.push({ path, before, after });
  }
  return out;
}

// Remove non-declarative fields so the frontend diff mirrors the backend.
const META_EXCLUDED = new Set([
  "_id", "updated_at", "revision_number", "latest_revision_id",
  "consultant_notes", "score", "assessments", "consistency", "comparative_plan",
  "crm_events", "crm_notes", "crm_status", "outputs",
]);
function stripMeta(c) {
  if (!c || typeof c !== "object") return c;
  const out = {};
  for (const k of Object.keys(c)) if (!META_EXCLUDED.has(k)) out[k] = c[k];
  return out;
}

// FASE E.2 Turno 3.3 HOTFIX MULTIDOC — bidirectional-controlled sync of
// DOC #1 (identification pane) → questioned_documents[0]. Only affects
// qds[0]; qds[1..N-1] are copied verbatim. Never truncates the array,
// never mutates stable ids. If qds is empty (legacy pre-multidoc case),
// leaves it empty. Empty legacy fields DO NOT overwrite non-empty
// canonical fields on qds[0]: we merge with identification winning only
// on the DOC #1 declarative fields the admin can actually edit in the
// identification pane.
function _syncQds0FromIdentification(caseObj) {
  if (!caseObj || typeof caseObj !== "object") return caseObj;
  const qds = Array.isArray(caseObj.questioned_documents) ? caseObj.questioned_documents : [];
  if (qds.length === 0) return caseObj;
  const ident = caseObj.identification || {};
  const first = qds[0] || {};
  // Fields that are edited via the identification pane. Falsy values from
  // identification (`null` / `undefined` / `""`) still overwrite qds[0]
  // because the admin can legitimately clear them; but we NEVER wipe the
  // stable `id` or `order` of qds[0].
  const mirrored = {
    ...first,
    id: first.id || (crypto?.randomUUID?.() || `qd-${Date.now()}`),
    order: 0,
    title: ident.title ?? first.title ?? "",
    role: ident.role || first.role || "principale",
    document_type: ident.document_type ?? first.document_type ?? "",
    date_value: ident.contested_document_date ?? first.date_value ?? "",
    // FASE E.2 Turno 3.5 HOTFIX — identification pane is the DOC #00
    // editing UI and offers only an ISO date input (no precision select).
    // Therefore when the admin sets/updates the ISO date, the intent is
    // ALWAYS exact — do NOT preserve a stale partial `date_precision`
    // that would fossilise a previously-partial state (regression
    // observed live: DOC #00 stayed "month_year Novembre 2025" even
    // after the admin typed a full exact date). Text/year/month partial
    // fields are cleared to keep the structured-date coherent.
    date_precision: ident.contested_document_date
      ? "exact"
      : (first.date_precision || "unknown"),
    date_text: ident.contested_document_date ? "" : (first.date_text || ""),
    date_year: ident.contested_document_date ? "" : (first.date_year || ""),
    date_month: ident.contested_document_date ? "" : (first.date_month || ""),
    material_support: ident.material_support ?? first.material_support ?? "",
    original_status: ident.original_status ?? first.original_status ?? "",
    custodian: ident.original_holder ?? first.custodian ?? "",
    disputed_signature_count: ident.disputed_signature_count ?? first.disputed_signature_count ?? null,
    contains_handwritten_text: ident.contains_handwritten_text ?? first.contains_handwritten_text ?? null,
    contains_handwritten_numbers: ident.contains_handwritten_numbers ?? first.contains_handwritten_numbers ?? null,
    contains_initials: ident.contains_initials ?? first.contains_initials ?? null,
    contains_full_signature: ident.contains_full_signature ?? first.contains_full_signature ?? null,
    contains_multiple_signatures: ident.contains_multiple_signatures ?? first.contains_multiple_signatures ?? null,
    contains_multiple_writers: ident.contains_multiple_writers ?? first.contains_multiple_writers ?? null,
    contested_area_description: ident.contested_area_description ?? first.contested_area_description ?? "",
    notes: ident.notes ?? first.notes ?? "",
  };
  return {
    ...caseObj,
    questioned_documents: [mirrored, ...qds.slice(1)],
  };
}

export default function AdminCaseEditor() {
  const { id: caseId } = useParams();
  const nav = useNavigate();
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [revisions, setRevisions] = useState([]);
  const [baseRevisionNumber, setBaseRevisionNumber] = useState(null);
  const [original, setOriginal] = useState(null);
  const [c, setC] = useState(null);
  const [section, setSection] = useState("requester");
  const [saving, setSaving] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [notes, setNotes] = useState([]);
  const [reviewStatus, setReviewStatus] = useState("not_reviewed");
  const [staleError, setStaleError] = useState(false);
  const [blockingError, setBlockingError] = useState(null);

  const allowed = user ? isAdmin : false;

  const load = async () => {
    setLoading(true); setStaleError(false); setBlockingError(null);
    try {
      const list = await adminListRevisions(caseId);
      const cur = list.current_revision_number || 1;
      const rev = await adminGetRevision(caseId, cur);
      const snap = normalizeCase(rev.snapshot || {});
      setRevisions(list.revisions || []);
      setBaseRevisionNumber(cur);
      setOriginal(snap);
      setC(JSON.parse(JSON.stringify(snap)));
      setReviewStatus(rev.review_status || "not_reviewed");
      const nl = await adminListNotes(caseId, { revision_number: cur });
      setNotes(nl.notes || []);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Fascicolo non trovato" : "Errore caricamento fascicolo");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (allowed) load(); else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, caseId]);

  const diffList = useMemo(() => {
    if (!original || !c) return [];
    return diffPaths(stripMeta(original), stripMeta(c));
  }, [original, c]);
  const hasChanges = diffList.length > 0;
  // FASE E.2 Turno 3.1 HOTFIX FINALE (V5) — pending report_review notes
  // targeting Rev(base+1), publishable, not archived. When present, they
  // enable revision creation even without declarative field changes. The
  // note itself is a publishable change of the case's report artefact.
  const pendingReportNotes = useMemo(() => {
    const targetRev = (baseRevisionNumber || 0) + 1;
    return (notes || []).filter((n) =>
      n && n.note_type === "report_review"
      && Number(n.target_revision_number) === targetRev
      && n.is_included_in_report === true
      && n.archived !== true
    );
  }, [notes, baseRevisionNumber]);
  const hasPendingReportNotes = pendingReportNotes.length > 0;
  const hasRevisionChanges = hasChanges || hasPendingReportNotes;

  const patch = (updater) => setC((prev) => {
    const next = JSON.parse(JSON.stringify(prev));
    updater(next);
    return next;
  });

  const confirmRevision = async () => {
    setSaving(true); setStaleError(false); setBlockingError(null);
    try {
      // FASE E.2 Turno 3.3 HOTFIX MULTIDOC — sync qds[0] (mirror of DOC #1)
      // from the identification pane BEFORE POST, so the persisted array
      // stays consistent (title, date, structural flags of DOC #1 always
      // match `identification.*`). We never truncate qds[1..N-1] here:
      // they carry the additional documents the user explicitly added.
      const synced = _syncQds0FromIdentification(c);
      const payload = {
        expected_revision_number: baseRevisionNumber,
        case: stripMeta(synced),
        review_status: reviewStatus,
      };
      const res = await adminCreateRevision(caseId, payload);
      toast.success(`Revisione R${res.revision.revision_number} creata`);
      setShowDiff(false);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail === "stale_revision") {
        setStaleError(true);
        toast.error("Il fascicolo è stato modificato altrove: ricarica per continuare");
      } else if (e?.response?.status === 409 && detail === "blocking_consistency") {
        setBlockingError("La coerenza documentale è bloccante: correggi i dati prima di confermare");
        toast.error("Incoerenza bloccante: revisione rifiutata");
      } else {
        toast.error("Errore creazione revisione");
      }
    } finally { setSaving(false); }
  };

  if (allowed === false) {
    return (
      <div className="min-h-screen bg-paper flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md" data-testid="editor-forbidden">
            <ShieldCheck className="text-slate-300 mx-auto" size={40} />
            <h1 className="font-serif text-2xl font-semibold mt-4">Accesso riservato</h1>
            <p className="text-sm text-slate-600 mt-2">Editor disponibile solo agli amministratori.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading || !c) {
    return (
      <div className="min-h-screen bg-paper flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin text-navy" size={28} data-testid="editor-loading" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10 w-full" data-testid="admin-case-editor">
        {/* Top bar */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <button onClick={() => nav("/admin/crm")}
              className="text-xs text-slate-500 hover:text-navy inline-flex items-center gap-1 mb-2"
              data-testid="editor-back">
              <ArrowLeft size={12} /> Torna al CRM
            </button>
            <div className="overline mb-1">Modifica / integra fascicolo</div>
            <h1 className="font-serif text-2xl md:text-4xl font-semibold tracking-tight" data-testid="editor-title">
              Editor amministratore — R{baseRevisionNumber}
            </h1>
            <p className="text-xs md:text-sm text-slate-600 mt-1">
              Modifica dei <b>dati di input</b>. Gli output (score, assumibilità, coerenza) verranno
              ricalcolati dal sistema alla conferma.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={saving}
              className="inline-flex items-center gap-1 border hairline hover:border-navy px-3 py-2 rounded-sm text-xs disabled:opacity-60"
              data-testid="editor-reload">
              <RefreshCw size={12} /> Ricarica
            </button>
            <button onClick={() => setShowDiff(true)} disabled={!hasRevisionChanges || saving}
              className="inline-flex items-center gap-1 bg-navy text-white hover:bg-navy-dark px-4 py-2 rounded-sm text-xs font-medium disabled:opacity-40"
              data-testid="editor-verify-changes">
              <CheckCircle2 size={13} /> Verifica modifiche ({diffList.length + pendingReportNotes.length})
            </button>
          </div>
        </div>

        {staleError && (
          <div className="mt-4 border-2 border-forensic-red bg-forensic-red/10 p-3 flex items-start gap-2" data-testid="editor-stale-warning">
            <Lock size={16} className="text-forensic-red shrink-0 mt-0.5" />
            <div className="text-xs md:text-sm text-slate-700 flex-1">
              <b>Fascicolo modificato altrove.</b> Un'altra revisione è stata creata dopo il caricamento.
              Ricarica il fascicolo per continuare.
            </div>
            <button onClick={load}
              className="text-xs bg-forensic-red text-white hover:opacity-90 px-3 py-1.5 rounded-sm"
              data-testid="editor-reload-stale">Ricarica fascicolo</button>
          </div>
        )}

        {/* Section tabs */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-8 border hairline bg-white overflow-hidden">
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => setSection(s.id)}
              className={`p-2 md:p-3 text-center border-r hairline last:border-r-0 transition-colors ${
                section === s.id ? "bg-navy text-white" : "text-slate-500 hover:bg-slate-50"
              }`}
              data-testid={`editor-tab-${s.id}`}>
              <div className="text-[10px] md:text-[11px] font-medium">{s.label}</div>
            </button>
          ))}
        </div>

        {/* Tab body */}
        <div className="border hairline bg-white mt-4 p-4 md:p-8">
          {section === "requester" && <SectionRequester c={c} patch={patch} />}
          {section === "assisted" && <SectionAssisted c={c} patch={patch} />}
          {section === "qdocs" && <SectionQdocs c={c} patch={patch} />}
          {section === "comparatives" && <SectionComparatives c={c} patch={patch} />}
          {section === "timeline" && <SectionTimeline c={c} patch={patch} />}
          {section === "clinical" && <SectionClinical c={c} patch={patch} />}
          {section === "strategy" && <SectionStrategy c={c} patch={patch} />}
          {section === "notes" && (
            <NotesSection caseId={caseId} baseRev={baseRevisionNumber}
              notes={notes} setNotes={setNotes} />
          )}
        </div>
      </main>
      <Footer />

      {showDiff && (
        <DiffModal
          diff={diffList}
          pendingReportNotes={pendingReportNotes}
          targetRevisionNumber={(baseRevisionNumber || 0) + 1}
          onClose={() => setShowDiff(false)}
          onConfirm={confirmRevision}
          saving={saving}
          blockingError={blockingError}
          staleError={staleError}
          reviewStatus={reviewStatus}
          setReviewStatus={setReviewStatus}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION COMPONENTS
// ---------------------------------------------------------------------------
function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid ? `${testid}-field` : undefined}>
      <div className="overline mb-2">{label}</div>
      {children}
    </label>
  );
}

function TextInput({ value, onChange, placeholder, testid, type = "text", ...rest }) {
  return (
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border hairline px-3 py-2 rounded-sm bg-white text-sm"
      data-testid={testid} {...rest} />
  );
}

function Select({ value, onChange, options, testid, hideEmpty, disabled = false }) {
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full border hairline px-3 py-2 rounded-sm bg-white text-sm disabled:bg-slate-100 disabled:text-slate-400"
      data-testid={testid}>
      {!hideEmpty && <option value="">— Seleziona —</option>}
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

function Bool3({ value, onChange, testid }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      {[[true, "Sì"], [false, "No"], [null, "?"]].map(([v, l]) => (
        <button key={String(v)} type="button"
          onClick={() => onChange(v)}
          data-testid={`${testid}-${String(v)}`}
          className={`px-3 py-1.5 rounded-sm text-sm border hairline transition-colors ${
            value === v ? "bg-navy text-white border-navy" : "bg-white hover:bg-slate-100"
          }`}>{l}</button>
      ))}
    </div>
  );
}

function Chk({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-navy" data-testid={testid} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

// Renders a date input group compliant with partial-date semantics (never
// injects proxy 01/06/YYYY). See FASE E.2 Turno 3 spec.
//
// FASE E.2 Turno 3.3 HOTFIX — precision-transition reset rules.
// Every precision change resets ONLY the fields that are semantically
// invalidated by the new precision, while preserving those that remain
// meaningful. The exact date value is NEVER auto-generated when moving
// from a partial precision to `exact` (the user must supply day/month).
function _resetForPrecision(newPrec, dateKey) {
  switch (newPrec) {
    case "exact":
      // Exact = day+month+year → user must retype; wipe partial fields.
      return { [dateKey]: "", date_text: "", date_year: "", date_month: "" };
    case "month_year":
      // Preserve date_year + date_month; reset ISO value + free text.
      return { [dateKey]: "", date_text: "" };
    case "year_only":
      // Preserve date_year only; reset month + ISO value + free text.
      return { [dateKey]: "", date_text: "", date_month: "" };
    case "estimated":
      // Preserve date_year + free text (e.g. "circa 2015"); drop month + ISO.
      return { [dateKey]: "", date_month: "" };
    case "unknown":
    default:
      return { [dateKey]: "", date_text: "", date_year: "", date_month: "" };
  }
}

function DateGroup({ item, patch, patchFn, onChange, dateKey = "date", testid }) {
  // FASE E.2 Turno 3.7 HOTFIX — the prop that carries the state updater
  // has historically been passed with three different names by the four
  // callers of DateGroup:
  //   * DOC #01+, comparatives, timeline, clinical-doc → `patchFn={...}`
  //   * (legacy)                                        → `patch={...}`
  //   * (defensive)                                     → `onChange={...}`
  // The previous Turno 3.6 signature only destructured `patch: patchFn`,
  // meaning that on the LIVE build EVERY caller (which passes `patchFn`)
  // received `patchFn === undefined` inside the component. The first
  // click on the precision Select therefore threw
  //   Uncaught TypeError: n is not a function
  //     at onChange (AdminCaseEditor.jsx:538 — inside the updater call)
  //     at onChange (AdminCaseEditor.jsx:434 — inside `<select onChange>`)
  // We now accept all three prop names and fail loudly if none is a
  // function so a caller regression is caught immediately in dev.
  const _apply = patchFn || patch || onChange;
  if (typeof _apply !== "function") {
    // Fail loud in dev, no-op in prod — never throw during render.
    // eslint-disable-next-line no-console
    if (typeof console !== "undefined") {
      console.error("[DateGroup] missing updater prop (patchFn / patch / onChange)");
    }
    return null;
  }
  const precision = item.date_precision || (item[dateKey] ? "exact" : "unknown");
  // FASE E.2 Turno 3.4 HOTFIX BUG A — `<input type="date">` silently rejects
  // any value that is not strictly `YYYY-MM-DD`. Legacy cases may persist
  // ISO strings with a time component (e.g. `2025-09-19T00:00:00.000Z`),
  // which caused the input to render EMPTY on the live editor and thus
  // made it appear "un-editable" (typing did trigger onChange but the
  // browser reset the value to "" on the next controlled re-render).
  // We defensively normalise to `YYYY-MM-DD` for the controlled `value`
  // and mirror the same on the onChange path.
  const _sanitizeIsoDate = (v) => {
    if (!v || typeof v !== "string") return "";
    const m = v.match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : "";
  };
  const _exactValue = _sanitizeIsoDate(item[dateKey]);
  // FASE E.2 Turno 3.6 HOTFIX — `input type="month"` returns a native
  // `YYYY-MM` value. When present we parse it into date_year/date_month
  // AND compose an Italian human-readable `date_text` (e.g. "Novembre 2025")
  // so downstream consumers keep the canonical presentation without
  // synthesising day-of-month.
  const _monthNamesIt = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio",
    "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
  const _monthValue = (item.date_year && item.date_month)
    ? `${String(item.date_year).padStart(4, "0")}-${String(item.date_month).padStart(2, "0")}`
    : "";
  const _parseMonthInput = (ym) => {
    if (!ym) return { date_year: "", date_month: "", date_text: "" };
    const m = ym.match(/^(\d{4})-(\d{2})$/);
    if (!m) return { date_year: "", date_month: "", date_text: "" };
    const y = m[1], mo = parseInt(m[2], 10);
    const label = _monthNamesIt[mo - 1] || "";
    return { date_year: y, date_month: String(mo),
             date_text: label ? `${label} ${y}` : "" };
  };
  return (
    <div className="space-y-2" data-dg-version="e2-turno-3.7">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-2">
        <Select value={precision}
          disabled={false}
          onChange={(v) => _apply({
            date_precision: v,
            ..._resetForPrecision(v, dateKey),
          })}
          options={DATE_PRECISION_OPTIONS}
          testid={`${testid}-date-precision`} hideEmpty />
        {precision === "exact" ? (
          <input type="date" min="1800-01-01" max="2200-12-31"
            value={_exactValue}
            onChange={(e) => _apply({ [dateKey]: _sanitizeIsoDate(e.target.value) })}
            className="border hairline px-3 py-2 rounded-sm bg-white text-sm font-mono"
            data-testid={`${testid}-date`} />
        ) : precision === "month_year" ? (
          // Native month picker → `YYYY-MM`. Parses into structured fields.
          <input type="month" min="1800-01" max="2200-12"
            value={_monthValue}
            onChange={(e) => _apply(_parseMonthInput(e.target.value))}
            className="border hairline px-3 py-2 rounded-sm bg-white text-sm font-mono"
            data-testid={`${testid}-date-month-picker`} />
        ) : precision === "year_only" || precision === "estimated" ? (
          // Year-only / estimated → dedicated numeric year input. `date_text`
          // is kept in sync so the PDF renders "2020" or "circa 2015".
          <input type="number" min="1800" max="2200"
            value={item.date_year || ""}
            onChange={(e) => {
              const y = e.target.value;
              _apply({
                date_year: y,
                date_text: y
                  ? (precision === "estimated" ? `circa ${y}` : String(y))
                  : "",
                date_month: "",
              });
            }}
            placeholder={precision === "estimated" ? "es. 2015 (circa)" : "es. 2025"}
            className="border hairline px-3 py-2 rounded-sm bg-white text-sm font-mono"
            data-testid={`${testid}-date-year-input`} />
        ) : (
          // FASE E.2 Turno 3.6 — `unknown` branch. We render a passive
          // helper (not a form input) so the admin can clearly see that
          // the value field is intentionally inert while the precision
          // Select on the LEFT remains active for changing precision.
          // Previously we rendered a `<input disabled value="Data non nota">`,
          // which visually looked identical to a "whole row disabled"
          // state and made the admin think the Select was also disabled
          // — this is the live-observed UX regression.
          <div className="border hairline border-dashed px-3 py-2 rounded-sm bg-slate-50 text-xs text-slate-500 italic flex items-center"
            data-testid={`${testid}-date-unknown-hint`}>
            Data non nota — seleziona una precisione a sinistra per inserire.
          </div>
        )}
      </div>
    </div>
  );
}

function SectionRequester({ c, patch }) {
  const r = c.requester || {};
  const set = (k, v) => patch((nc) => { nc.requester = { ...nc.requester, [k]: v }; });
  return (
    <div className="space-y-6" data-testid="section-requester">
      <div>
        <div className="overline mb-3">Percorso / Ruolo utente</div>
        <div className="flex flex-wrap gap-2">
          {[
            { v: "privato", l: "Cliente privato" },
            { v: "avvocato", l: "Avvocato" },
            { v: "professionista", l: "Altro professionista" },
          ].map((o) => (
            <button key={o.v} type="button"
              onClick={() => patch((nc) => { nc.role = o.v; })}
              className={`text-xs px-4 py-2 rounded-sm border transition-colors ${
                c.role === o.v ? "bg-navy text-white border-navy" : "bg-white hairline hover:border-navy"
              }`}
              data-testid={`editor-role-${o.v}`}>{o.l}</button>
          ))}
        </div>
      </div>
      <div className="border hairline p-4 bg-paper">
        <div className="overline mb-3">Dati del richiedente</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Nome"><TextInput value={r.first_name} onChange={(v) => set("first_name", v)} testid="req-first-name" /></Field>
          <Field label="Cognome"><TextInput value={r.last_name} onChange={(v) => set("last_name", v)} testid="req-last-name" /></Field>
          {c.role === "avvocato" && (
            <>
              <Field label="Titolo">
                <Select value={r.title || "Avv."} onChange={(v) => set("title", v)}
                  options={LEGAL_TITLES} testid="req-title" hideEmpty />
              </Field>
              <Field label="Studio / Organizzazione">
                <TextInput value={r.organization} onChange={(v) => set("organization", v)} testid="req-organization" />
              </Field>
            </>
          )}
          <Field label="E-mail"><TextInput type="email" value={r.email} onChange={(v) => set("email", v)} testid="req-email" /></Field>
          <Field label="Telefono"><TextInput type="tel" value={r.phone} onChange={(v) => set("phone", v)} testid="req-phone" /></Field>
        </div>
      </div>
      <Field label="Breve descrizione del caso (max 1.000 caratteri)">
        <textarea value={c.case_short_description || ""}
          onChange={(e) => patch((nc) => { nc.case_short_description = e.target.value.slice(0, 1000); })}
          rows={5} maxLength={1000}
          className="w-full border hairline px-3 py-2 rounded-sm bg-white text-sm resize-y"
          data-testid="editor-case-description" />
      </Field>
    </div>
  );
}

function SectionAssisted({ c, patch }) {
  const ap = c.assisted_party || {};
  const subject = c.identification?.subject || {};
  const ident = c.identification || {};
  const setAp = (k, v) => patch((nc) => { nc.assisted_party = { ...nc.assisted_party, [k]: v }; });
  const setSubj = (k, v) => patch((nc) => {
    nc.identification = { ...nc.identification,
      subject: { ...(nc.identification?.subject || {}), [k]: v } };
  });
  const setIdent = (k, v) => patch((nc) => { nc.identification = { ...nc.identification, [k]: v }; });

  const rolesList = c.role === "privato" ? PRIVATE_CASE_ROLES : ASSISTED_PARTY_ROLES;

  return (
    <div className="space-y-6" data-testid="section-assisted">
      <div className="border hairline p-4 bg-paper">
        <div className="overline mb-3">Parte assistita nel procedimento</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Nome"><TextInput value={ap.first_name} onChange={(v) => setAp("first_name", v)} testid="ap-first-name" /></Field>
          <Field label="Cognome"><TextInput value={ap.last_name} onChange={(v) => setAp("last_name", v)} testid="ap-last-name" /></Field>
          <Field label="Posizione processuale">
            <Select value={ap.role || ""} onChange={(v) => setAp("role", v)} options={rolesList} testid="ap-role" />
          </Field>
          {ap.role === "altro" && (
            <Field label="Specifica posizione"><TextInput value={ap.role_other} onChange={(v) => setAp("role_other", v)} testid="ap-role-other" /></Field>
          )}
        </div>
      </div>
      <div className="border hairline p-4 bg-paper">
        <div className="overline mb-3">Soggetto scrivente</div>
        <p className="text-[11px] text-slate-500 mb-3 italic">
          La data di nascita vuota <b>resta vuota</b>: non viene mai autocompilata con la data odierna.
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Nome del soggetto"><TextInput value={subject.name} onChange={(v) => setSubj("name", v)} testid="subj-name" /></Field>
          <Field label="Data di nascita">
            <input type="date" min="1800-01-01" max="2200-12-31"
              value={subject.dob || ""}
              onChange={(e) => setSubj("dob", e.target.value)}
              className="w-full border hairline px-3 py-2 rounded-sm bg-white font-mono text-sm"
              data-testid="subj-dob" />
          </Field>
          <Field label="Data del decesso (se applicabile)">
            <input type="date" min="1800-01-01" max="2200-12-31"
              value={subject.deceased_date || ""}
              onChange={(e) => setSubj("deceased_date", e.target.value)}
              className="w-full border hairline px-3 py-2 rounded-sm bg-white font-mono text-sm"
              data-testid="subj-deceased" />
          </Field>
          <Field label="Età dichiarata (fallback)">
            <TextInput type="number" value={ident.subject_age_years ?? ""}
              onChange={(v) => setIdent("subject_age_years", v === "" ? null : Number(v))}
              testid="subj-age" />
          </Field>
          <Field label="Lateralità">
            <Select value={ident.laterality || ""} onChange={(v) => setIdent("laterality", v)}
              options={[
                { v: "destro", l: "Destrimane" },
                { v: "mancino", l: "Mancino" },
                { v: "ambidestro", l: "Ambidestro" },
                { v: "non_noto", l: "Non noto" },
              ]} testid="subj-laterality" />
          </Field>
          <Field label="Uso di occhiali">
            <Bool3 value={ident.uses_glasses ?? null} onChange={(v) => setIdent("uses_glasses", v)} testid="subj-glasses" />
          </Field>
        </div>
      </div>
    </div>
  );
}

function SectionQdocs({ c, patch }) {
  const ident = c.identification || {};
  const qds = c.questioned_documents || [];
  const setIdent = (kv) => patch((nc) => { nc.identification = { ...nc.identification, ...kv }; });
  // FASE E.2 Turno 3.3 HOTFIX MULTIDOC — qds[0] is the persistent mirror
  // of the identification pane ("Doc #1 principale"). We MUST NOT surface
  // it as an editable card in "Ulteriori documenti in verifica" because:
  //   * the user would see DOC #1 rendered twice (identification + qds[0]);
  //   * deleting the apparent duplicate would drop `qds.length` from 2 to
  //     1, causing the PDF renderer to fall back to the legacy single-doc
  //     branch (regression observed on the live fascicolo Testamento +
  //     Dichiarazione privata: multi → mono after admin revision).
  // "Ulteriori" always operates on qds[1..N-1]. The synchronisation from
  // identification back into qds[0] happens once at commit time inside
  // `confirmRevision` — see comment there.
  const additionalQds = qds.length > 0 ? qds.slice(1) : [];
  const updQ = (i, kv) => patch((nc) => {
    // `i` is the 0-based index into the ADDITIONAL slice. Real array
    // position is `i + 1` (0 is reserved for the identification mirror).
    nc.questioned_documents = (nc.questioned_documents || []).map(
      (x, k) => k === i + 1 ? { ...x, ...kv } : x);
  });
  const addQ = () => patch((nc) => {
    // Ensure the identification mirror exists at qds[0] before appending.
    const existing = nc.questioned_documents || [];
    const withMirror = existing.length > 0
      ? existing
      : [{ id: mkId("qd"), order: 0, title: nc.identification?.title || "",
           role: nc.identification?.role || "principale" }];
    nc.questioned_documents = [...withMirror, {
      id: mkId("qd"),
      title: "", role: "secondario", document_type: "",
      date_value: "", date_precision: "unknown", date_text: "", date_year: "", date_month: "",
      material_support: "", original_status: "", custodian: "",
      disputed_signature_count: null,
      contains_handwritten_text: null, contains_handwritten_numbers: null,
      contains_initials: null, contains_full_signature: null,
      contains_multiple_signatures: null, contains_multiple_writers: null,
      contested_area_description: "", notes: "", order: withMirror.length,
    }];
  });
  const rmQ = (i) => patch((nc) => {
    // Same offset: never remove qds[0] here (that would drop DOC #1).
    nc.questioned_documents = (nc.questioned_documents || []).filter(
      (_, k) => k !== i + 1);
  });

  return (
    <div className="space-y-6" data-testid="section-qdocs">
      {/* Doc #1 = identification */}
      <div className="border hairline p-4 bg-paper" data-testid="editor-qdoc-primary">
        <div className="overline mb-3">Documento in verifica #1 (principale)</div>
        <div className="grid md:grid-cols-[2fr_1fr] gap-2">
          <TextInput value={ident.title} onChange={(v) => setIdent({ title: v })}
            placeholder="Denominazione" testid="qd0-title" />
          <Select value={ident.role || "principale"} onChange={(v) => setIdent({ role: v })}
            options={QDOC_ROLES} testid="qd0-role" hideEmpty />
        </div>
        <div className="grid md:grid-cols-2 gap-2 mt-2">
          <Select value={ident.document_type} onChange={(v) => setIdent({ document_type: v })}
            options={DOC_TYPES} testid="qd0-doctype" />
          {/* FASE E.2 Turno 3.5 HOTFIX BUG A — DOC #00 date input MUST
              consume strict `YYYY-MM-DD`. Legacy snapshots occasionally
              persisted ISO-with-time (`2025-09-19T00:00:00.000Z`) which
              `<input type="date">` silently rejects → the live editor
              rendered an empty field that appeared un-editable. We
              sanitise on both the controlled `value` and the onChange
              write path (mirrors the DateGroup fix from Turno 3.4). */}
          <input type="date" min="1800-01-01" max="2200-12-31"
            value={(() => {
              const v = ident.contested_document_date || "";
              const m = typeof v === "string" ? v.match(/^(\d{4}-\d{2}-\d{2})/) : null;
              return m ? m[1] : "";
            })()}
            onChange={(e) => {
              const v = e.target.value || "";
              const m = v.match(/^(\d{4}-\d{2}-\d{2})/);
              setIdent({ contested_document_date: m ? m[1] : "" });
            }}
            className="border hairline px-3 py-2 rounded-sm text-sm font-mono bg-white"
            data-testid="qd0-date" />
        </div>
        <div className="grid md:grid-cols-2 gap-2 mt-2">
          <Select value={ident.material_support || ""}
            onChange={(v) => setIdent({ material_support: v, document_availability: v })}
            options={QDOC_MATERIAL} testid="qd0-material" />
          <Select value={ident.original_status || ""}
            onChange={(v) => setIdent({ original_status: v })}
            options={QDOC_ORIGINAL_STATUS} testid="qd0-original-status" />
        </div>
        <TextInput value={ident.original_holder} onChange={(v) => setIdent({ original_holder: v })}
          placeholder="Custode" testid="qd0-holder" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3 text-xs">
          <Chk label="Testo manoscritto" checked={ident.contains_handwritten_text} onChange={(v) => setIdent({ contains_handwritten_text: v })} testid="qd0-htext" />
          <Chk label="Numeri manoscritti" checked={ident.contains_handwritten_numbers} onChange={(v) => setIdent({ contains_handwritten_numbers: v })} testid="qd0-hnum" />
          <Chk label="Sigle/paraffe" checked={ident.contains_initials} onChange={(v) => setIdent({ contains_initials: v })} testid="qd0-init" />
          <Chk label="Firma per esteso" checked={ident.contains_full_signature} onChange={(v) => setIdent({ contains_full_signature: v })} testid="qd0-fullsig" />
          <Chk label="Più firme" checked={ident.contains_multiple_signatures} onChange={(v) => setIdent({ contains_multiple_signatures: v })} testid="qd0-multisig" />
          <Chk label="Più scriventi" checked={ident.contains_multiple_writers} onChange={(v) => setIdent({ contains_multiple_writers: v })} testid="qd0-multiwr" />
        </div>
        <div className="grid md:grid-cols-3 gap-3 mt-3">
          <Field label="N° firme contestate">
            <TextInput type="number" value={ident.disputed_signature_count ?? ""}
              onChange={(v) => setIdent({ disputed_signature_count: v === "" ? null : parseInt(v) })}
              testid="qd0-sigcount" />
          </Field>
          <Field label="Fase procedurale">
            <Select value={ident.phase || "stragiudiziale"} onChange={(v) => setIdent({ phase: v })}
              options={PHASE_OPTIONS} testid="qd0-phase" hideEmpty />
          </Field>
          <Field label="Prodotto in giudizio?">
            <Bool3 value={ident.already_in_court} onChange={(v) => setIdent({ already_in_court: v })} testid="qd0-court" />
          </Field>
        </div>
        <TextInput value={ident.contested_area_description} onChange={(v) => setIdent({ contested_area_description: v })}
          placeholder="Zona contestata" testid="qd0-area" />
        <TextInput value={ident.notes} onChange={(v) => setIdent({ notes: v })}
          placeholder="Note" testid="qd0-notes" />
      </div>
      {/* Doc #2+ */}
      <div className="flex items-center justify-between">
        <div className="overline">Ulteriori documenti in verifica ({additionalQds.length})</div>
        <button type="button" onClick={addQ}
          className="text-xs text-navy hover:underline inline-flex items-center gap-1"
          data-testid="editor-add-qdoc">
          <Plus size={12} /> Aggiungi documento
        </button>
      </div>
      {additionalQds.length === 0 ? (
        <div className="text-xs text-slate-500 italic">Nessun documento aggiuntivo.</div>
      ) : (
        additionalQds.map((qd, i) => (
          <div key={qd.id} className="border hairline p-4 bg-paper space-y-2" data-testid={`editor-qdoc-${i}`}>
            <div className="flex items-center justify-between">
              <div className="overline">Documento in verifica #{i + 2}</div>
              <button type="button" onClick={() => rmQ(i)}
                className="text-slate-500 hover:text-forensic-red"
                data-testid={`editor-qdoc-${i}-rm`}><Trash2 size={14} /></button>
            </div>
            <div className="grid md:grid-cols-[2fr_1fr] gap-2">
              <TextInput value={qd.title} onChange={(v) => updQ(i, { title: v })} placeholder="Denominazione" testid={`editor-qdoc-${i}-title`} />
              <Select value={qd.role || "secondario"} onChange={(v) => updQ(i, { role: v })} options={QDOC_ROLES} testid={`editor-qdoc-${i}-role`} hideEmpty />
            </div>
            <TextInput value={qd.document_type} onChange={(v) => updQ(i, { document_type: v })} placeholder="Tipo documento" testid={`editor-qdoc-${i}-type`} />
            <DateGroup item={qd} dateKey="date_value"
              patchFn={(kv) => updQ(i, kv)} testid={`editor-qdoc-${i}`} />
            <div className="grid md:grid-cols-2 gap-2">
              <Select value={qd.material_support || ""} onChange={(v) => updQ(i, { material_support: v })} options={QDOC_MATERIAL} testid={`editor-qdoc-${i}-material`} />
              <Select value={qd.original_status || ""} onChange={(v) => updQ(i, { original_status: v })} options={QDOC_ORIGINAL_STATUS} testid={`editor-qdoc-${i}-orig`} />
            </div>
            <TextInput value={qd.custodian} onChange={(v) => updQ(i, { custodian: v })} placeholder="Custode" testid={`editor-qdoc-${i}-cust`} />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <Chk label="Testo manoscritto" checked={qd.contains_handwritten_text} onChange={(v) => updQ(i, { contains_handwritten_text: v })} testid={`editor-qdoc-${i}-htext`} />
              <Chk label="Numeri manoscritti" checked={qd.contains_handwritten_numbers} onChange={(v) => updQ(i, { contains_handwritten_numbers: v })} testid={`editor-qdoc-${i}-hnum`} />
              <Chk label="Sigle/paraffe" checked={qd.contains_initials} onChange={(v) => updQ(i, { contains_initials: v })} testid={`editor-qdoc-${i}-init`} />
              <Chk label="Firma per esteso" checked={qd.contains_full_signature} onChange={(v) => updQ(i, { contains_full_signature: v })} testid={`editor-qdoc-${i}-fullsig`} />
              <Chk label="Più firme" checked={qd.contains_multiple_signatures} onChange={(v) => updQ(i, { contains_multiple_signatures: v })} testid={`editor-qdoc-${i}-multisig`} />
              <Chk label="Più scriventi" checked={qd.contains_multiple_writers} onChange={(v) => updQ(i, { contains_multiple_writers: v })} testid={`editor-qdoc-${i}-multiwr`} />
            </div>
            <TextInput value={qd.notes} onChange={(v) => updQ(i, { notes: v })} placeholder="Note" testid={`editor-qdoc-${i}-notes`} />
          </div>
        ))
      )}
    </div>
  );
}

function SectionComparatives({ c, patch }) {
  const comps = c.comparatives?.comparatives || [];
  const period = c.comparatives?.period_covered_years;
  const verified = c.comparatives?.already_verified_documents;

  const upd = (i, kv) => patch((nc) => {
    nc.comparatives = { ...(nc.comparatives || {}),
      comparatives: (nc.comparatives?.comparatives || []).map((x, k) => k === i ? { ...x, ...kv } : x) };
  });
  const add = () => patch((nc) => {
    nc.comparatives = { ...(nc.comparatives || {}),
      comparatives: [...(nc.comparatives?.comparatives || []), {
        id: mkId("cmp"),
        description: "", year: null, is_original: false, is_official: false,
        contains_signature: true, contains_text: false, contains_numbers: false,
        certainly_attributable: true, contains_full_signature: false,
        date: "", date_precision: "unknown", date_text: "", date_year: "", date_month: "",
      }] };
  });
  const rm = (i) => patch((nc) => {
    nc.comparatives = { ...(nc.comparatives || {}),
      comparatives: (nc.comparatives?.comparatives || []).filter((_, k) => k !== i) };
  });
  const setMeta = (k, v) => patch((nc) => {
    nc.comparatives = { ...(nc.comparatives || {}), [k]: v };
  });

  return (
    <div className="space-y-4" data-testid="section-comparatives">
      <div className="flex items-center justify-between">
        <div className="overline">Comparative dichiarate ({comps.length})</div>
        <button type="button" onClick={add}
          className="text-xs text-navy hover:underline inline-flex items-center gap-1"
          data-testid="editor-add-comp">
          <Plus size={12} /> Aggiungi comparativa
        </button>
      </div>
      {comps.map((cm, i) => (
        <div key={cm.id} className="border hairline p-4 bg-paper space-y-2" data-testid={`editor-comp-${i}`}>
          <div className="flex items-center justify-between">
            <div className="overline">Comparativa #{String(i + 1).padStart(2, "0")}</div>
            <button type="button" onClick={() => rm(i)}
              className="text-slate-500 hover:text-forensic-red"
              data-testid={`editor-comp-${i}-rm`}><Trash2 size={14} /></button>
          </div>
          <TextInput value={cm.description} onChange={(v) => upd(i, { description: v })}
            placeholder="Descrizione (es. rogito notarile, contratto banca...)"
            testid={`editor-comp-${i}-desc`} />
          <DateGroup item={cm} dateKey="date"
            patchFn={(kv) => upd(i, { ...kv, year: kv.date_year ? Number(kv.date_year) : cm.year })}
            testid={`editor-comp-${i}`} />
          <div className="grid md:grid-cols-2 gap-2">
            <Select value={cm.source_material || ""}
              onChange={(v) => upd(i, { source_material: v, is_original: v === "originale" })}
              options={SOURCE_MATERIALS} testid={`editor-comp-${i}-support`} />
            <TextInput value={cm.custodian} onChange={(v) => upd(i, { custodian: v })}
              placeholder="Custode / provenienza" testid={`editor-comp-${i}-cust`} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            <Chk label="Ufficiale" checked={cm.is_official} onChange={(v) => upd(i, { is_official: v })} testid={`editor-comp-${i}-official`} />
            <Chk label="Attribuibile con certezza" checked={cm.certainly_attributable} onChange={(v) => upd(i, { certainly_attributable: v })} testid={`editor-comp-${i}-attrib`} />
            <Chk label="Firma per esteso" checked={cm.contains_full_signature} onChange={(v) => upd(i, { contains_full_signature: v })} testid={`editor-comp-${i}-fullsig`} />
            <Chk label="Contiene testo" checked={cm.contains_text} onChange={(v) => upd(i, { contains_text: v })} testid={`editor-comp-${i}-text`} />
            <Chk label="Contiene numeri" checked={cm.contains_numbers} onChange={(v) => upd(i, { contains_numbers: v })} testid={`editor-comp-${i}-num`} />
            <Chk label="Sigla / firma breve" checked={cm.is_short_signature} onChange={(v) => upd(i, { is_short_signature: v })} testid={`editor-comp-${i}-short`} />
          </div>
        </div>
      ))}
      <div className="grid md:grid-cols-2 gap-3 pt-4 border-t hairline">
        <Field label="Periodo temporale coperto (anni)">
          <TextInput type="number" value={period ?? ""}
            onChange={(v) => setMeta("period_covered_years", v ? Number(v) : null)}
            testid="editor-comp-period" />
        </Field>
        <div className="flex items-end">
          <Chk label="Sono presenti documenti già verificati/autenticati"
            checked={verified} onChange={(v) => setMeta("already_verified_documents", v)}
            testid="editor-comp-verified" />
        </div>
      </div>
    </div>
  );
}

function SectionTimeline({ c, patch }) {
  const events = c.timeline || [];
  const upd = (i, kv) => patch((nc) => {
    nc.timeline = (nc.timeline || []).map((x, k) => k === i ? { ...x, ...kv } : x);
  });
  const add = () => patch((nc) => {
    nc.timeline = [...(nc.timeline || []), {
      id: mkId("ev"), event_macro: "", event_subtype: "",
      title: "", description: "", location: "", source: "",
      date: "", date_precision: "unknown", date_text: "", date_year: "", date_month: "",
      notes: "",
    }];
  });
  const rm = (i) => patch((nc) => {
    nc.timeline = (nc.timeline || []).filter((_, k) => k !== i);
  });

  return (
    <div className="space-y-4" data-testid="section-timeline">
      <div className="flex items-center justify-between">
        <div className="overline">Eventi cronologia ({events.length})</div>
        <button type="button" onClick={add}
          className="text-xs text-navy hover:underline inline-flex items-center gap-1"
          data-testid="editor-add-event">
          <Plus size={12} /> Aggiungi evento
        </button>
      </div>
      {events.map((ev, i) => (
        <div key={ev.id} className="border hairline p-4 bg-paper space-y-2" data-testid={`editor-event-${i}`}>
          <div className="flex items-center justify-between">
            <div className="overline">Evento #{String(i + 1).padStart(2, "0")}</div>
            <button type="button" onClick={() => rm(i)}
              className="text-slate-500 hover:text-forensic-red"
              data-testid={`editor-event-${i}-rm`}><Trash2 size={14} /></button>
          </div>
          <Select value={ev.event_macro || ""} onChange={(v) => upd(i, { event_macro: v, event_subtype: "" })}
            options={[{ v: "", l: "— Tipo di evento —" }, ...TIMELINE_MACROS]}
            testid={`editor-event-${i}-macro`} hideEmpty />
          <TextInput value={ev.title} onChange={(v) => upd(i, { title: v })}
            placeholder="Titolo breve" testid={`editor-event-${i}-title`} />
          <textarea value={ev.description || ""}
            onChange={(e) => upd(i, { description: e.target.value })}
            placeholder="Descrizione"
            rows={2}
            className="w-full border hairline px-3 py-2 rounded-sm bg-white text-sm resize-y"
            data-testid={`editor-event-${i}-desc`} />
          <DateGroup item={ev} dateKey="date" patchFn={(kv) => upd(i, kv)} testid={`editor-event-${i}`} />
          <TextInput value={ev.location} onChange={(v) => upd(i, { location: v })}
            placeholder="Luogo (facoltativo)" testid={`editor-event-${i}-loc`} />
          <TextInput value={ev.notes} onChange={(v) => upd(i, { notes: v })}
            placeholder="Note (facoltativo)" testid={`editor-event-${i}-notes`} />
        </div>
      ))}
      {events.length === 0 && (
        <div className="text-xs text-slate-500 italic">Nessun evento in cronologia.</div>
      )}
    </div>
  );
}

function SectionClinical({ c, patch }) {
  const cl = c.clinical || {};
  const docs = cl.documents || [];
  const ricoveri = cl.ricoveri || [];
  const conditions = cl.conditions || [];

  const setNotes = (v) => patch((nc) => { nc.clinical = { ...nc.clinical, notes: v }; });
  const toggleCond = (cond) => patch((nc) => {
    const cur = nc.clinical?.conditions || [];
    nc.clinical = { ...nc.clinical,
      conditions: cur.includes(cond) ? cur.filter((x) => x !== cond) : [...cur, cond] };
  });
  const updDoc = (i, kv) => patch((nc) => {
    nc.clinical = { ...nc.clinical,
      documents: (nc.clinical?.documents || []).map((x, k) => k === i ? { ...x, ...kv } : x) };
  });
  const addDoc = () => patch((nc) => {
    nc.clinical = { ...nc.clinical,
      documents: [...(nc.clinical?.documents || []), {
        id: mkId("clindoc"), type: "", type_label: "", type_other: "",
        title: "", event_description: "", outcome: "",
        date: "", date_precision: "unknown", date_text: "", date_year: "", date_month: "",
        notes: "",
      }] };
  });
  const rmDoc = (i) => patch((nc) => {
    nc.clinical = { ...nc.clinical,
      documents: (nc.clinical?.documents || []).filter((_, k) => k !== i) };
  });
  const updRic = (i, kv) => patch((nc) => {
    nc.clinical = { ...nc.clinical,
      ricoveri: (nc.clinical?.ricoveri || []).map((x, k) => k === i ? { ...x, ...kv } : x) };
  });
  const addRic = () => patch((nc) => {
    nc.clinical = { ...nc.clinical,
      ricoveri: [...(nc.clinical?.ricoveri || []), {
        id: mkId("ricov"), start_date: "", end_date: "", structure: "",
        documentation_available: false,
      }] };
  });
  const rmRic = (i) => patch((nc) => {
    nc.clinical = { ...nc.clinical,
      ricoveri: (nc.clinical?.ricoveri || []).filter((_, k) => k !== i) };
  });

  return (
    <div className="space-y-6" data-testid="section-clinical">
      <div>
        <div className="overline mb-2">Condizioni dichiarate</div>
        <div className="flex flex-wrap gap-2">
          {CLINICAL_CONDITIONS.map((cond) => {
            const on = conditions.includes(cond);
            return (
              <button key={cond} type="button" onClick={() => toggleCond(cond)}
                className={`text-xs px-3 py-1 rounded-sm border transition-colors ${
                  on ? "bg-navy text-white border-navy" : "bg-white hairline hover:border-navy"
                }`}
                data-testid={`editor-cond-${cond}`}>{cond}</button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="overline">Documentazione sanitaria ({docs.length})</div>
          <button type="button" onClick={addDoc}
            className="text-xs text-navy hover:underline inline-flex items-center gap-1"
            data-testid="editor-add-clindoc">
            <Plus size={12} /> Aggiungi documento
          </button>
        </div>
        {docs.map((d, i) => (
          <div key={d.id} className="border hairline p-4 bg-paper space-y-2 mb-2" data-testid={`editor-clindoc-${i}`}>
            <div className="flex items-center justify-between">
              <Select value={d.type || ""} onChange={(v) => {
                  const opt = CLINICAL_DOC_TYPES.find((t) => t.v === v);
                  updDoc(i, { type: v, type_label: opt?.l || "" });
                }} options={CLINICAL_DOC_TYPES} testid={`editor-clindoc-${i}-type`} />
              <button type="button" onClick={() => rmDoc(i)}
                className="text-slate-500 hover:text-forensic-red ml-2"
                data-testid={`editor-clindoc-${i}-rm`}><Trash2 size={14} /></button>
            </div>
            <DateGroup item={d} dateKey="date" patchFn={(kv) => updDoc(i, kv)} testid={`editor-clindoc-${i}`} />
            <TextInput value={d.title} onChange={(v) => updDoc(i, { title: v })} placeholder="Titolo" testid={`editor-clindoc-${i}-title`} />
            <TextInput value={d.event_description} onChange={(v) => updDoc(i, { event_description: v })} placeholder="Evento descritto" testid={`editor-clindoc-${i}-event`} />
            <TextInput value={d.outcome} onChange={(v) => updDoc(i, { outcome: v })} placeholder="Esito" testid={`editor-clindoc-${i}-outcome`} />
          </div>
        ))}
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="overline">Ricoveri ({ricoveri.length})</div>
          <button type="button" onClick={addRic}
            className="text-xs text-navy hover:underline inline-flex items-center gap-1"
            data-testid="editor-add-ricovero">
            <Plus size={12} /> Aggiungi ricovero
          </button>
        </div>
        {ricoveri.map((r, i) => (
          <div key={r.id} className="border hairline p-3 bg-paper grid grid-cols-1 md:grid-cols-[7rem_7rem_1fr_auto_auto] gap-2 items-center mb-2" data-testid={`editor-ricovero-${i}`}>
            <input type="date" min="1800-01-01" max="2200-12-31"
              value={r.start_date || ""}
              onChange={(e) => updRic(i, { start_date: e.target.value })}
              className="border hairline px-2 py-1.5 rounded-sm text-xs font-mono bg-white"
              data-testid={`editor-ricovero-${i}-start`} />
            <input type="date" min="1800-01-01" max="2200-12-31"
              value={r.end_date || ""}
              onChange={(e) => updRic(i, { end_date: e.target.value })}
              className="border hairline px-2 py-1.5 rounded-sm text-xs font-mono bg-white"
              data-testid={`editor-ricovero-${i}-end`} />
            <TextInput value={r.structure} onChange={(v) => updRic(i, { structure: v })}
              placeholder="Struttura" testid={`editor-ricovero-${i}-struct`} />
            <Chk label="Docs disp." checked={r.documentation_available}
              onChange={(v) => updRic(i, { documentation_available: v })}
              testid={`editor-ricovero-${i}-docs`} />
            <button type="button" onClick={() => rmRic(i)}
              className="text-slate-500 hover:text-forensic-red p-1"
              data-testid={`editor-ricovero-${i}-rm`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <Field label="Note cliniche">
        <textarea value={cl.notes || ""}
          onChange={(e) => setNotes(e.target.value)} rows={3}
          className="w-full border hairline px-3 py-2 rounded-sm bg-white text-sm resize-y"
          data-testid="editor-clinical-notes" />
      </Field>
    </div>
  );
}

function SectionStrategy({ c, patch }) {
  const s = c.legal_strategy || {};
  const set = (k, v) => patch((nc) => { nc.legal_strategy = { ...nc.legal_strategy, [k]: v }; });
  return (
    <div className="space-y-4" data-testid="section-strategy">
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Parte producente"><TextInput value={s.producing_party} onChange={(v) => set("producing_party", v)} testid="editor-strat-producing" /></Field>
        <Field label="Parte contraria"><TextInput value={s.against_party} onChange={(v) => set("against_party", v)} testid="editor-strat-against" /></Field>
        <Field label="Stato sottoscrizione">
          <Select value={s.signature_status || ""} onChange={(v) => set("signature_status", v)}
            options={SIGNATURE_STATUS_OPTIONS} testid="editor-strat-sigstatus" />
        </Field>
        <Field label="Termini processuali">
          <TextInput value={s.deadline_notes} onChange={(v) => set("deadline_notes", v)} testid="editor-strat-deadline" />
        </Field>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Istanza verificazione">
          <Bool3 value={s.verificazione_requested} onChange={(v) => set("verificazione_requested", v)} testid="editor-strat-verif" />
        </Field>
        <Field label="Querela di falso">
          <Bool3 value={s.querela_falso} onChange={(v) => set("querela_falso", v)} testid="editor-strat-querela" />
        </Field>
        <Field label="CTU disposta">
          <Bool3 value={s.ctu_disposed} onChange={(v) => set("ctu_disposed", v)} testid="editor-strat-ctu" />
        </Field>
      </div>
      <Field label="Note disconoscimento">
        <textarea value={s.disconoscimento_note || ""}
          onChange={(e) => set("disconoscimento_note", e.target.value)} rows={3}
          className="w-full border hairline px-3 py-2 rounded-sm bg-white text-sm resize-y"
          data-testid="editor-strat-disc-note" />
      </Field>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NOTES SECTION — consultant notes (internal / report_review)
// ---------------------------------------------------------------------------
function NotesSection({ caseId, baseRev, notes, setNotes }) {
  const [text, setText] = useState("");
  const [type, setType] = useState("internal");
  const [inReport, setInReport] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  // FASE E.2 Turno 3.1 — read-only diagnostic trace panel (no fix behavior).
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceRev, setTraceRev] = useState(baseRev);
  const [traceData, setTraceData] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceError, setTraceError] = useState(null);

  const fetchTrace = async () => {
    setTraceLoading(true); setTraceError(null); setTraceData(null);
    try {
      const res = await api.get(`/admin/cases/${caseId}/revisions/${traceRev}/notes-trace`);
      setTraceData(res.data);
    } catch (e) {
      setTraceError(e?.response?.data?.detail || e?.message || "errore trace");
    } finally { setTraceLoading(false); }
  };

  const copyTrace = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(traceData, null, 2));
      toast.success("JSON copiato negli appunti");
    } catch { toast.error("Copia non riuscita"); }
  };

  const create = async () => {
    if (!text.trim()) { toast.error("Testo nota obbligatorio"); return; }
    setSaving(true);
    try {
      // FASE E.2 Turno 3.1 FINAL — target publication anchor.
      // report_review notes authored in the editor are destined for the
      // revision being prepared (base + 1). internal notes stay on base.
      const target = type === "report_review" ? baseRev + 1 : baseRev;
      const payload = {
        note_type: type, text: text.trim(),
        revision_number: baseRev,
        target_revision_number: target,
        is_included_in_report: type === "report_review" ? inReport : false,
      };
      const n = await adminCreateNote(caseId, payload);
      setNotes([...notes, n]);
      setText(""); setInReport(false);
      toast.success("Nota consulente aggiunta");
    } catch { toast.error("Errore creazione nota"); }
    finally { setSaving(false); }
  };

  const toggleInReport = async (n) => {
    try {
      const updated = await adminUpdateNote(caseId, n.id, { is_included_in_report: !n.is_included_in_report });
      setNotes(notes.map((x) => x.id === n.id ? updated : x));
      toast.success("Nota aggiornata");
    } catch { toast.error("Errore aggiornamento"); }
  };

  const archive = async (n) => {
    try {
      const updated = await adminUpdateNote(caseId, n.id, { archived: true });
      setNotes(notes.map((x) => x.id === n.id ? updated : x));
      toast.success("Nota archiviata");
    } catch { toast.error("Errore archiviazione"); }
  };

  const saveEdit = async (n, newText) => {
    try {
      const updated = await adminUpdateNote(caseId, n.id, { text: newText });
      setNotes(notes.map((x) => x.id === n.id ? updated : x));
      setEditing(null);
      toast.success("Nota aggiornata");
    } catch { toast.error("Errore aggiornamento"); }
  };

  const internal = notes.filter((n) => n.note_type === "internal" && !n.archived);
  const forReport = notes.filter((n) => n.note_type === "report_review" && !n.archived);

  return (
    <div className="space-y-6" data-testid="section-notes">
      <div className="border hairline p-4 bg-paper">
        <div className="overline mb-3">Aggiungi nota consulente</div>
        <div className="flex gap-2 mb-3">
          <button type="button" onClick={() => setType("internal")}
            className={`text-xs px-3 py-1.5 rounded-sm border ${type === "internal" ? "bg-navy text-white border-navy" : "hairline bg-white"}`}
            data-testid="editor-note-type-internal">Nota interna (solo admin)</button>
          <button type="button" onClick={() => setType("report_review")}
            className={`text-xs px-3 py-1.5 rounded-sm border ${type === "report_review" ? "bg-navy text-white border-navy" : "hairline bg-white"}`}
            data-testid="editor-note-type-review">Osservazione per il report</button>
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)}
          rows={4} placeholder={type === "internal" ? "Nota tecnica riservata (mai pubblicata nel PDF)…" : "Osservazione professionale (potrà essere pubblicata nel report)…"}
          className="w-full border hairline px-3 py-2 rounded-sm bg-white text-sm resize-y"
          data-testid="editor-note-text" />
        {type === "report_review" && (
          <label className="inline-flex items-center gap-2 mt-2 cursor-pointer text-xs" data-testid="editor-note-in-report-label">
            <input type="checkbox" checked={inReport} onChange={(e) => setInReport(e.target.checked)}
              className="accent-navy" data-testid="editor-note-in-report" />
            <span>Includi nel PDF (sezione "Osservazioni del consulente")</span>
          </label>
        )}
        <div className="mt-3">
          <button onClick={create} disabled={saving || !text.trim()}
            className="bg-navy hover:bg-navy-dark text-white px-4 py-2 rounded-sm text-xs font-medium disabled:opacity-50"
            data-testid="editor-note-create">
            {saving ? "Salvataggio..." : "Aggiungi nota"}
          </button>
        </div>
      </div>

      <NotesList title={`Note interne (${internal.length})`} testid="editor-internal-notes"
        notes={internal} editing={editing} setEditing={setEditing}
        onArchive={archive} onSaveEdit={saveEdit} showToggleInReport={false} />

      <NotesList title={`Osservazioni per il report (${forReport.length})`} testid="editor-review-notes"
        notes={forReport} editing={editing} setEditing={setEditing}
        onArchive={archive} onSaveEdit={saveEdit}
        showToggleInReport={true} onToggleInReport={toggleInReport} />

      {/* FASE E.2 Turno 3.1 — Diagnostic trace panel (read-only). */}
      <div className="border-2 border-dashed hairline bg-paper p-4" data-testid="editor-notes-trace">
        <div className="flex items-center justify-between mb-2">
          <div className="overline">Diagnostica note (read-only)</div>
          <button type="button" onClick={() => setTraceOpen((v) => !v)}
            className="text-xs text-navy hover:underline"
            data-testid="editor-notes-trace-toggle">
            {traceOpen ? "Nascondi" : "Mostra"}
          </button>
        </div>
        {traceOpen && (
          <div className="space-y-2">
            <p className="text-[11px] text-slate-500 italic">
              Restituisce esattamente cosa contiene il DB, il filtro di pubblicazione
              e la snapshot per la revisione indicata. Nessuna modifica di dati.
            </p>
            <div className="flex items-center gap-2 text-xs">
              <label>Revisione:</label>
              <input type="number" min={1} value={traceRev}
                onChange={(e) => setTraceRev(parseInt(e.target.value) || 1)}
                className="border hairline px-2 py-1 rounded-sm w-20 font-mono"
                data-testid="editor-notes-trace-rev" />
              <button onClick={fetchTrace} disabled={traceLoading}
                className="bg-navy hover:bg-navy-dark text-white px-3 py-1 rounded-sm text-xs disabled:opacity-50"
                data-testid="editor-notes-trace-fetch">
                {traceLoading ? "Caricamento…" : "Esegui trace"}
              </button>
              {traceData && (
                <button onClick={copyTrace}
                  className="border hairline hover:border-navy px-3 py-1 rounded-sm text-xs"
                  data-testid="editor-notes-trace-copy">Copia JSON</button>
              )}
            </div>
            {traceError && (
              <div className="text-xs text-forensic-red border-l-2 border-forensic-red pl-2 py-1"
                data-testid="editor-notes-trace-error">{String(traceError)}</div>
            )}
            {traceData && (
              <pre className="text-[10px] font-mono bg-white border hairline p-2 max-h-96 overflow-auto whitespace-pre-wrap break-words"
                data-testid="editor-notes-trace-json">
{JSON.stringify(traceData, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function NotesList({ title, notes, editing, setEditing, onArchive, onSaveEdit,
                     showToggleInReport, onToggleInReport, testid }) {
  return (
    <div data-testid={testid}>
      <div className="overline mb-2">{title}</div>
      {notes.length === 0 ? (
        <div className="text-xs text-slate-500 italic">Nessuna nota.</div>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => (
            <div key={n.id} className="border hairline p-3 bg-white" data-testid={`editor-note-${n.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[11px] text-slate-500 font-mono">
                  {new Date(n.created_at).toLocaleString("it-IT")} · {n.created_by?.email || "—"}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {showToggleInReport && (
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={!!n.is_included_in_report}
                        onChange={() => onToggleInReport(n)}
                        className="accent-navy" data-testid={`editor-note-${n.id}-in-report`} />
                      <span className={n.is_included_in_report ? "text-forensic-green font-medium" : "text-slate-500"}>
                        {n.is_included_in_report ? "Nel PDF" : "Non nel PDF"}
                      </span>
                    </label>
                  )}
                  <button onClick={() => setEditing(editing === n.id ? null : n.id)}
                    className="text-navy hover:underline"
                    data-testid={`editor-note-${n.id}-edit`}>
                    {editing === n.id ? "Annulla" : "Modifica"}
                  </button>
                  <button onClick={() => onArchive(n)}
                    className="text-slate-500 hover:text-forensic-red"
                    data-testid={`editor-note-${n.id}-archive`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              {editing === n.id ? (
                <EditableNote initialText={n.text} onSave={(t) => onSaveEdit(n, t)} />
              ) : (
                <p className="text-sm whitespace-pre-wrap">{n.text}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditableNote({ initialText, onSave }) {
  const [t, setT] = useState(initialText);
  return (
    <div className="space-y-2">
      <textarea value={t} onChange={(e) => setT(e.target.value)}
        rows={3}
        className="w-full border hairline px-2 py-1.5 rounded-sm bg-paper text-sm resize-y" />
      <button onClick={() => onSave(t)}
        className="text-xs bg-navy text-white px-3 py-1 rounded-sm hover:bg-navy-dark">
        Salva modifica
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DIFF MODAL
// ---------------------------------------------------------------------------
function DiffModal({ diff, pendingReportNotes = [], targetRevisionNumber,
                     onClose, onConfirm, saving, blockingError, staleError,
                     reviewStatus, setReviewStatus }) {
  const canConfirm = (diff.length > 0 || pendingReportNotes.length > 0)
    && !saving && !blockingError && !staleError;
  const grouped = useMemo(() => {
    // Group by top-level section for readability.
    const g = {};
    diff.forEach((d) => {
      const first = d.path.split(".")[0].replace(/\[.*/, "");
      const section = ({
        requester: "Richiedente", assisted_party: "Parte assistita",
        identification: "Documenti / Scrivente", questioned_documents: "Documenti in verifica",
        comparatives: "Comparative", timeline: "Cronologia",
        clinical: "Clinico", legal_strategy: "Strategia",
        case_short_description: "Descrizione caso", role: "Percorso",
      })[first] || first;
      if (!g[section]) g[section] = [];
      g[section].push(d);
    });
    return g;
  }, [diff]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 md:p-4" data-testid="diff-modal">
      <div className="bg-white max-w-4xl w-full max-h-[92vh] flex flex-col shadow-2xl">
        <div className="p-4 md:p-6 border-b hairline flex items-start justify-between gap-3">
          <div>
            <div className="overline">Verifica modifiche</div>
            <h2 className="font-serif text-xl md:text-2xl font-semibold mt-1">Riepilogo revisione</h2>
            <p className="text-xs md:text-sm text-slate-600 mt-1">
              {diff.length === 0 && pendingReportNotes.length === 0
                ? "Nessuna modifica dichiarata."
                : `${diff.length} modifica/e ai dati dichiarati · ${pendingReportNotes.length} osservazione/i consulente da pubblicare. La coerenza documentale verrà validata alla conferma; gli output verranno ricalcolati automaticamente.`}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-forensic-red text-xl leading-none"
            data-testid="diff-close">×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
          {blockingError && (
            <div className="border-2 border-forensic-red bg-forensic-red/10 p-3 flex items-start gap-2" data-testid="diff-blocking">
              <AlertTriangle size={16} className="text-forensic-red shrink-0 mt-0.5" />
              <p className="text-xs md:text-sm text-slate-700">{blockingError}</p>
            </div>
          )}
          {staleError && (
            <div className="border-2 border-forensic-red bg-forensic-red/10 p-3 flex items-start gap-2">
              <Lock size={16} className="text-forensic-red shrink-0 mt-0.5" />
              <p className="text-xs md:text-sm text-slate-700">
                Il fascicolo è stato modificato da un'altra sessione. Chiudi e ricarica.
              </p>
            </div>
          )}
          {diff.length === 0 && pendingReportNotes.length === 0 && (
            <div className="text-sm text-slate-500 italic">Non risultano differenze rispetto alla revisione corrente.</div>
          )}
          {pendingReportNotes.length > 0 && (
            <div className="border-2 border-forensic-green bg-forensic-green/5" data-testid="diff-pending-notes">
              <div className="border-b hairline bg-forensic-green/10 px-3 py-2 overline">
                Osservazioni del consulente da pubblicare · destinate a Revisione n. {targetRevisionNumber} ({pendingReportNotes.length})
              </div>
              <div className="divide-y hairline">
                {pendingReportNotes.map((n, i) => (
                  <div key={n.id || i} className="p-3 text-xs md:text-sm" data-testid={`diff-pending-note-${i}`}>
                    <div className="text-[10px] uppercase text-forensic-green tracking-widest mb-1">Pubblicazione nel report</div>
                    <div className="text-slate-800 whitespace-pre-wrap break-words">{n.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {Object.entries(grouped).map(([section, entries]) => (
            <div key={section} className="border hairline bg-paper" data-testid={`diff-section-${section}`}>
              <div className="border-b hairline bg-navy/5 px-3 py-2 overline">{section} ({entries.length})</div>
              <div className="divide-y hairline">
                {entries.map((e, i) => (
                  <div key={i} className="p-3 text-xs md:text-sm" data-testid={`diff-entry-${section}-${i}`}>
                    <div className="font-medium text-slate-700">{humanizeFieldPath(e.path)}</div>
                    <div className="grid md:grid-cols-2 gap-2 mt-1">
                      <div className="border-l-2 border-slate-300 bg-white pl-2 py-1">
                        <span className="text-[10px] uppercase text-slate-500 tracking-widest">Prima</span>
                        <div className="text-slate-700 break-words">{humanizeValue(e.path, e.before)}</div>
                      </div>
                      <div className="border-l-2 border-forensic-green bg-white pl-2 py-1">
                        <span className="text-[10px] uppercase text-forensic-green tracking-widest">Dopo</span>
                        <div className="text-slate-800 break-words font-medium">{humanizeValue(e.path, e.after)}</div>
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono mt-1">codice interno: {e.path}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 md:p-6 border-t hairline flex flex-wrap items-center justify-between gap-3 bg-slate-50">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-500">Stato revisione:</span>
            <select value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)}
              className="border hairline px-2 py-1 rounded-sm bg-white"
              data-testid="diff-review-status">
              <option value="not_reviewed">Non revisionato</option>
              <option value="in_review">In revisione</option>
              <option value="reviewed">Revisionato</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="border hairline px-4 py-2 rounded-sm text-xs hover:bg-slate-100"
              data-testid="diff-cancel">Annulla</button>
            <button onClick={onConfirm} disabled={!canConfirm}
              className="bg-navy hover:bg-navy-dark text-white px-4 py-2 rounded-sm text-xs font-medium disabled:opacity-40 inline-flex items-center gap-1"
              data-testid="diff-confirm">
              {saving ? <><Loader2 size={12} className="animate-spin" /> Salvataggio…</> : <><Save size={12} /> Conferma revisione</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
